import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  TrendingUp, Loader2, Plus, Building2, Target, Award,
  ChevronDown, ChevronRight, Sparkles, AlertCircle, CheckCircle, Clock
} from "lucide-react";

const CompetitorAnalysisPage = () => {
  const { t } = useTranslation('audit');
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedComp, setExpandedComp] = useState({});
  const [form, setForm] = useState({ industry: "", focus_areas: [], num_competitors: 10 });

  const INDUSTRIES = [
    "Medical Devices", "Pharmaceuticals & Biotech", "Aerospace & Defense",
    "Automotive", "Industrial Manufacturing", "Electronics & Telecom",
    "Food & Beverage", "Energy & Utilities",
    "Brazil / LATAM", "Chemical & Process", "Construction & Infrastructure",
    "Software & SaaS",
  ];
  const FOCUS_AREAS = [
    "QMS Implementation", "Regulatory Compliance", "Audit Services",
    "Training & Education", "Digital QMS Tools", "Supplier Management",
    "Risk Management", "Process Optimization",
  ];

  useEffect(() => { fetchAnalyses(); }, []);

  useEffect(() => {
    if (!selected || selected.status !== "generating") return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/competitor-analyses/${selected.analysis_id}`, { withCredentials: true });
        if (res.data.status !== "generating") {
          setSelected(res.data);
          fetchAnalyses();
          clearInterval(interval);
        }
      } catch (e) { clearInterval(interval); }
    }, 5000);
    return () => clearInterval(interval);
  }, [selected]);

  const fetchAnalyses = async () => {
    try {
      const res = await axios.get(`${API}/competitor-analyses`, { withCredentials: true });
      setAnalyses(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const toggleFocus = (f) => {
    setForm(p => ({
      ...p, focus_areas: p.focus_areas.includes(f) ? p.focus_areas.filter(x => x !== f) : [...p.focus_areas, f]
    }));
  };

  const handleGenerate = async () => {
    if (!form.industry) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/competitor-analysis/generate`, form, { withCredentials: true });
      setSelected(res.data);
      setShowForm(false);
      fetchAnalyses();
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const positionColor = { leader: "#4ADE80", challenger: "#FBBF24", niche: "#38BDF8" };

  return (
    <QMSLayout title={t('common:modules.audit')}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white" data-testid="ca-title">Market Intelligence</h2>
          <p className="text-[#8B95A5] text-sm mt-1">AI-powered competitive landscape analysis</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="ca-new-btn">
          <Plus size={16} /> New Analysis
        </button>
      </div>

      {showForm && (
        <div className="qms-card mb-6" data-testid="ca-form">
          <div className="qms-card-body space-y-4">
            <h3 className="font-['Syne'] font-semibold text-white flex items-center gap-2">
              <Sparkles size={18} className="text-[#E8A838]" /> Competitor Analysis
            </h3>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider mb-2 block">Industry</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ca-industry-select">
                {INDUSTRIES.map(ind => (
                  <button key={ind} onClick={() => setForm(p => ({ ...p, industry: ind }))}
                    className={`px-3 py-2 rounded text-xs font-medium transition-all ${
                      form.industry === ind ? "bg-[#36A9E1] text-white" : "bg-[#1F222D] text-[#8B95A5] hover:bg-[#2D313D]"
                    }`}>{ind}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider mb-2 block">Focus Areas (multi-select)</label>
              <div className="flex flex-wrap gap-2" data-testid="ca-focus-select">
                {FOCUS_AREAS.map(f => (
                  <button key={f} onClick={() => toggleFocus(f)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                      form.focus_areas.includes(f) ? "bg-[#E8A838] text-black" : "bg-[#1F222D] text-[#8B95A5] hover:bg-[#2D313D]"
                    }`}>{f}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleGenerate} disabled={generating || !form.industry}
                className="btn-primary flex items-center gap-2" data-testid="ca-generate-btn">
                {generating ? <><Loader2 size={16} className="animate-spin" /> Analyzing...</> : <><TrendingUp size={16} /> Analyze</>}
              </button>
              <button onClick={() => setShowForm(false)} className="btn-ghost">{t('common:actions.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {selected && selected.status === "generating" && (
        <div className="qms-card mb-6"><div className="qms-card-body flex items-center gap-3 text-[#FBBF24] justify-center py-6">
          <Loader2 size={20} className="animate-spin" /><span className="text-sm">AI is analyzing the competitive landscape...</span>
        </div></div>
      )}

      {selected && selected.status === "completed" && (
        <div className="space-y-6" data-testid="ca-results">
          {/* Summary */}
          {selected.summary && (
            <div className="qms-card"><div className="qms-card-body">
              <h3 className="font-['Syne'] font-semibold text-white mb-3">Market Summary — {selected.industry}</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-[#36A9E1] text-xs uppercase tracking-wider mb-2">Trends</h4>
                  <ul className="space-y-1">{(selected.summary.market_trends || []).map((t, i) => (
                    <li key={i} className="text-[#8B95A5] text-xs flex items-start gap-2">
                      <TrendingUp size={12} className="text-[#4ADE80] mt-0.5 shrink-0" />{t}
                    </li>
                  ))}</ul>
                </div>
                <div>
                  <h4 className="text-[#E8A838] text-xs uppercase tracking-wider mb-2">Recommendations</h4>
                  <ul className="space-y-1">{(selected.summary.recommendations || []).map((r, i) => (
                    <li key={i} className="text-[#8B95A5] text-xs flex items-start gap-2">
                      <Target size={12} className="text-[#E8A838] mt-0.5 shrink-0" />{r}
                    </li>
                  ))}</ul>
                </div>
              </div>
            </div></div>
          )}
          {/* Competitors */}
          <div className="space-y-3">
            {(selected.competitors || []).map((comp, i) => (
              <div key={i} className="qms-card" data-testid={`ca-competitor-${i}`}>
                <div className="qms-card-body">
                  <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedComp(p => ({ ...p, [i]: !p[i] }))}>
                    <div className="flex items-center gap-3">
                      <Building2 size={16} className="text-[#36A9E1]" />
                      <h4 className="text-white font-medium text-sm">{comp.name}</h4>
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{
                        color: positionColor[comp.market_position] || "#8B95A5",
                        backgroundColor: `${positionColor[comp.market_position] || "#8B95A5"}20`
                      }}>{comp.market_position}</span>
                    </div>
                    {expandedComp[i] ? <ChevronDown size={14} className="text-[#8B95A5]" /> : <ChevronRight size={14} className="text-[#8B95A5]" />}
                  </div>
                  {expandedComp[i] && (
                    <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.06)] grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-[#8B95A5] text-xs mb-2">{comp.description}</p>
                        <h5 className="text-[#4ADE80] text-[10px] uppercase tracking-wider mb-1">Strengths</h5>
                        <ul className="space-y-0.5">{(comp.strengths || []).map((s, j) => (
                          <li key={j} className="text-[#8B95A5] text-xs">+ {s}</li>
                        ))}</ul>
                      </div>
                      <div>
                        <h5 className="text-[#F87171] text-[10px] uppercase tracking-wider mb-1">Weaknesses</h5>
                        <ul className="space-y-0.5">{(comp.weaknesses || []).map((w, j) => (
                          <li key={j} className="text-[#8B95A5] text-xs">- {w}</li>
                        ))}</ul>
                        <h5 className="text-[#38BDF8] text-[10px] uppercase tracking-wider mt-2 mb-1">Certifications</h5>
                        <div className="flex flex-wrap gap-1">{(comp.certifications || []).map((c, j) => (
                          <span key={j} className="sector-tag text-[10px]">{c}</span>
                        ))}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!selected && !showForm && (
        <div className="space-y-3">
          {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
           : analyses.length === 0 ? (
            <div className="qms-card"><div className="qms-card-body text-center text-[#8B95A5] py-12">
              <TrendingUp size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No analyses yet. Click "New Analysis" to get started.</p>
            </div></div>
          ) : analyses.map(a => (
            <div key={a.analysis_id} onClick={() => setSelected(a)}
              className="qms-card cursor-pointer hover:border-[#36A9E1]/30 transition-all" data-testid={`ca-item-${a.analysis_id}`}>
              <div className="qms-card-body py-3 flex items-center justify-between">
                <div>
                  <h4 className="text-white text-sm font-medium">{a.industry}</h4>
                  <p className="text-[#8B95A5] text-xs">{(a.competitors || []).length} competitors analyzed</p>
                </div>
                <span className={`text-xs font-medium ${a.status === "completed" ? "text-[#4ADE80]" : a.status === "generating" ? "text-[#FBBF24]" : "text-[#F87171]"}`}>
                  {a.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </QMSLayout>
  );
};

export default CompetitorAnalysisPage;
